/* ══════════════════════════════════════════════════════════════════
   MISE Kitchen Co. — Interactions & Motion Layer  v2.0
   Navy + Electric Blue design system
   ══════════════════════════════════════════════════════════════════
   Modules:
     1. Scroll Reveal      — [data-reveal] IntersectionObserver
     2. Count-Up           — animated stat numbers in hero / features
     3. Hero Parallax      — subtle depth on scroll
     4. Cart Pulse         — bubble animation on add-to-cart
     5. Nav polish         — underline class + keyboard a11y
     6. Gallery crossfade  — smooth thumbnail switch
     7. Blob idle jitter   — randomise blob animation offsets
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  (function initScrollReveal() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -48px 0px' }
    );
    function observeAll() {
      document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach(function (el) {
        observer.observe(el);
      });
    }
    observeAll();
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', observeAll);
    }
  })();

  (function initCountUp() {
    if (!('IntersectionObserver' in window)) return;
    var DURATION = 1400;
    function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }
    function parseNumber(text) {
      var match = text.match(/^[\d,]+(\.[\d]+)?/);
      if (!match) return null;
      return parseFloat(match[0].replace(/,/g, ''));
    }
    function buildDisplayText(original, currentVal) {
      return original.replace(/^([\d,]+(\.[\d]+)?)/, function (_, num) {
        var decimals = (num.indexOf('.') !== -1) ? num.split('.')[1].length : 0;
        var formatted = currentVal.toFixed(decimals);
        if (num.indexOf(',') !== -1) {
          formatted = parseFloat(formatted).toLocaleString('en-GB', {
            minimumFractionDigits: decimals, maximumFractionDigits: decimals
          });
        }
        return formatted;
      });
    }
    function animateCount(el) {
      var original = el.dataset.countupTarget || el.textContent.trim();
      var end = parseNumber(original);
      if (!end || end <= 0) return;
      el.dataset.countupTarget = original;
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var elapsed = ts - start;
        var progress = Math.min(elapsed / DURATION, 1);
        el.textContent = buildDisplayText(original, end * easeOutQuart(progress));
        if (progress < 1) { requestAnimationFrame(step); } else { el.textContent = original; }
      }
      requestAnimationFrame(step);
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { animateCount(entry.target); observer.unobserve(entry.target); }
        });
      },
      { threshold: 0.5 }
    );
    var selectors = ['[data-count-up]', '.mise-hero__stat-num', '.mise-reviews__score'].join(',');
    document.querySelectorAll(selectors).forEach(function (el) {
      if (parseNumber(el.textContent.trim())) observer.observe(el);
    });
  })();

  (function initHeroParallax() {
    var hero = document.querySelector('.mise-hero');
    if (!hero) return;
    var media = hero.querySelector('.mise-hero__media');
    var blobs = hero.querySelectorAll('.mise-blob');
    if (!media && !blobs.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var heroTop = 0, heroH = 0, ticking = false;
    function measure() {
      var rect = hero.getBoundingClientRect();
      heroTop = rect.top + window.scrollY;
      heroH = hero.offsetHeight;
    }
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          var scrolled = window.scrollY - heroTop;
          var progress = scrolled / (heroH || 1);
          if (progress >= 0 && progress <= 1.2) {
            if (media) media.style.transform = 'translateY(' + (scrolled * 0.12).toFixed(1) + 'px)';
            blobs.forEach(function (blob, i) {
              blob.style.transform = 'translateY(' + (scrolled * (i === 0 ? 0.07 : -0.05)).toFixed(1) + 'px)';
            });
          }
          ticking = false;
        });
        ticking = true;
      }
    }
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
  })();

  (function initCartPulse() {
    function pulse() {
      var bubble = document.querySelector('.cart-count-bubble');
      if (!bubble) return;
      bubble.classList.remove('mise-cart-pulse');
      void bubble.offsetWidth;
      bubble.classList.add('mise-cart-pulse');
    }
    document.addEventListener('cart:updated', pulse);
    document.addEventListener('product-added-to-cart', pulse);
    var _fetch = window.fetch;
    window.fetch = function (url, options) {
      var p = _fetch.apply(this, arguments);
      if (typeof url === 'string' && url.includes('/cart/add')) {
        p.then(function (r) { if (r.ok) setTimeout(pulse, 200); return r; });
      }
      return p;
    };
  })();

  (function initNavPolish() {
    document.querySelectorAll(
      '.header__menu-item > .header__menu-item-link, .list-menu__item--link'
    ).forEach(function (link) { link.classList.add('mise-nav-link'); });
    var nav = document.querySelector('header-drawer');
    if (nav) {
      document.addEventListener('click', function (e) {
        if (!nav.contains(e.target) && nav.hasAttribute('open')) {
          var closeBtn = nav.querySelector('[data-close-modal]');
          if (closeBtn) closeBtn.click();
        }
      });
    }
  })();

  (function initGalleryCrossfade() {
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('.thumbnail-list__item .thumbnail');
      if (!thumb) return;
      var gallery = thumb.closest('media-gallery');
      if (!gallery) return;
      var mainImg = gallery.querySelector('.product__media-item .media img, .product__media-item .media video');
      if (!mainImg) return;
      mainImg.style.transition = 'opacity 0.18s ease';
      mainImg.style.opacity = '0.3';
      setTimeout(function () { mainImg.style.opacity = '1'; }, 180);
    });
  })();

  (function initBlobJitter() {
    document.querySelectorAll('.mise-blob').forEach(function (blob) {
      if (blob.style.animationDelay) return;
      blob.style.animationDelay = '-' + (Math.random() * 6).toFixed(2) + 's';
    });
  })();

  (function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var id = link.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      var headerH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80', 10
      );
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - headerH - 16, behavior: 'smooth' });
    });
  })();

  (function injectFallbackCSS() {
    if (document.getElementById('mise-interactions-css')) return;
    var style = document.createElement('style');
    style.id = 'mise-interactions-css';
    style.textContent = [
      '[data-reveal]{opacity:0;transform:translateY(20px);transition:opacity .6s cubic-bezier(.16,1,.3,1),transform .6s cubic-bezier(.16,1,.3,1)}',
      '[data-reveal].is-visible{opacity:1;transform:none}',
      '@keyframes mise-cart-pulse{0%{transform:scale(1)}40%{transform:scale(1.45)}70%{transform:scale(.9)}100%{transform:scale(1)}}',
      '.mise-cart-pulse{animation:mise-cart-pulse .5s cubic-bezier(.36,.07,.19,.97)}',
      '.mise-nav-link{position:relative}',
      '.mise-nav-link::after{content:"";position:absolute;bottom:-2px;left:0;width:0;height:1.5px;background:#2458E6;transition:width .25s cubic-bezier(.16,1,.3,1)}',
      '.mise-nav-link:hover::after,.mise-nav-link[aria-current="page"]::after{width:100%}',
      'html.is-scrolled .header{padding-top:8px !important;padding-bottom:8px !important}'
    ].join('');
    document.head.appendChild(style);
  })();

})();
